import * as React from 'react';
import { LayoutGrid, Sliders } from 'lucide-react';
import { TEMPLATE_MANIFEST, type TemplateId } from '@repo/api-client';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

/**
 * Floating bottom-anchored pill toolbar for the invoice preview.
 *
 * Three pills: Template / Color / Options. Each opens a Radix popover
 * with the relevant controls. The toolbar is non-modal — the preview
 * iframe behind it keeps re-rendering as the user clicks, so they can
 * see each choice land instantly.
 *
 * Mirrors the visual + interaction model of mobile's
 * `apps/mobile/features/preview/customizeSheets.tsx` while using
 * desktop-native primitives (popovers instead of bottom sheets).
 */
const ACCENTS = [
  '#0EA5A5',
  '#C97651',
  '#A87521',
  '#324E68',
  '#1F5230',
  '#7B2D54',
  '#1F6FEB',
  '#0F1320',
] as const;

export interface CustomizeToolbarProps {
  templateId: TemplateId;
  onPickTemplate: (id: TemplateId) => void;
  accent: string;
  onPickAccent: (color: string) => void;
  showLogo: boolean;
  onToggleShowLogo: (next: boolean) => void;
  showFooter: boolean;
  onToggleShowFooter: (next: boolean) => void;
  showItemDiscount: boolean;
  onToggleShowItemDiscount: (next: boolean) => void;
  showItemTax: boolean;
  onToggleShowItemTax: (next: boolean) => void;
  showItemNotes: boolean;
  onToggleShowItemNotes: (next: boolean) => void;
  saving?: boolean;
  className?: string;
}

export function CustomizeToolbar({
  templateId,
  onPickTemplate,
  accent,
  onPickAccent,
  showLogo,
  onToggleShowLogo,
  showFooter,
  onToggleShowFooter,
  showItemDiscount,
  onToggleShowItemDiscount,
  showItemTax,
  onToggleShowItemTax,
  showItemNotes,
  onToggleShowItemNotes,
  saving,
  className,
}: CustomizeToolbarProps) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center',
        className,
      )}
    >
      <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-line bg-background/95 px-1.5 py-1 shadow-lg shadow-black/15 backdrop-blur">
        <Popover>
          <PopoverTrigger asChild>
            <Pill icon={<LayoutGrid className="size-3.5" />} label="Template" />
          </PopoverTrigger>
          <PopoverContent
            side="top"
            sideOffset={10}
            className="w-[560px] max-w-[92vw] p-3"
          >
            <TemplatePanel currentId={templateId} onPick={onPickTemplate} />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Pill
              icon={
                <span
                  className="size-3.5 rounded-full border border-black/20"
                  style={{ backgroundColor: accent }}
                />
              }
              label="Color"
            />
          </PopoverTrigger>
          <PopoverContent side="top" sideOffset={10} className="w-[280px] p-3">
            <ColorPanel value={accent} onPick={onPickAccent} />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Pill icon={<Sliders className="size-3.5" />} label="Options" />
          </PopoverTrigger>
          <PopoverContent side="top" sideOffset={10} className="w-[300px] p-3">
            <OptionsPanel
              showLogo={showLogo}
              onToggleShowLogo={onToggleShowLogo}
              showFooter={showFooter}
              onToggleShowFooter={onToggleShowFooter}
              showItemDiscount={showItemDiscount}
              onToggleShowItemDiscount={onToggleShowItemDiscount}
              showItemTax={showItemTax}
              onToggleShowItemTax={onToggleShowItemTax}
              showItemNotes={showItemNotes}
              onToggleShowItemNotes={onToggleShowItemNotes}
            />
          </PopoverContent>
        </Popover>

        {saving ? (
          <span className="ml-1 mr-2 text-[10px] text-muted-foreground">
            Saving…
          </span>
        ) : null}
      </div>
    </div>
  );
}

type PillProps = {
  icon: React.ReactNode;
  label: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const Pill = React.forwardRef<HTMLButtonElement, PillProps>(function Pill(
  { icon, label, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-foreground/90 transition hover:bg-secondary',
        'data-[state=open]:bg-secondary',
        className,
      )}
      {...rest}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
});

function TemplatePanel({
  currentId,
  onPick,
}: {
  currentId: TemplateId;
  onPick: (id: TemplateId) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Template
      </div>
      <div className="grid max-h-[300px] grid-cols-3 gap-2 overflow-y-auto">
        {TEMPLATE_MANIFEST.map((tpl) => {
          const on = tpl.id === currentId;
          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => onPick(tpl.id)}
              aria-pressed={on}
              className={cn(
                'flex flex-col items-stretch gap-1 rounded-lg border p-1.5 text-left transition',
                on
                  ? 'border-foreground ring-1 ring-foreground/20'
                  : 'border-line hover:border-line-strong',
              )}
            >
              <div
                className="aspect-120/170 overflow-hidden rounded bg-secondary"
                dangerouslySetInnerHTML={{ __html: tpl.thumbnail }}
              />
              <span className="px-0.5 text-[11px] font-medium text-foreground">
                {tpl.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ColorPanel({
  value,
  onPick,
}: {
  value: string;
  onPick: (color: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Accent
      </div>
      <div className="flex flex-wrap gap-2">
        {ACCENTS.map((color) => {
          const on = sameColor(color, value);
          return (
            <button
              key={color}
              type="button"
              aria-label={`Use ${color}`}
              aria-pressed={on}
              onClick={() => onPick(color)}
              className={cn(
                'size-8 rounded-full transition',
                on
                  ? 'ring-2 ring-offset-2 ring-foreground/60 ring-offset-background'
                  : 'hover:scale-105',
              )}
              style={{ backgroundColor: color }}
            />
          );
        })}
      </div>
      <label className="flex items-center justify-between rounded-md border border-line px-2 py-1.5 text-xs">
        <span>Custom</span>
        <input
          type="color"
          value={normaliseHex(value)}
          onChange={(e) => onPick(e.target.value)}
          className="h-6 w-9 cursor-pointer rounded border border-line p-0"
        />
      </label>
    </div>
  );
}

function OptionsPanel({
  showLogo,
  onToggleShowLogo,
  showFooter,
  onToggleShowFooter,
  showItemDiscount,
  onToggleShowItemDiscount,
  showItemTax,
  onToggleShowItemTax,
  showItemNotes,
  onToggleShowItemNotes,
}: {
  showLogo: boolean;
  onToggleShowLogo: (next: boolean) => void;
  showFooter: boolean;
  onToggleShowFooter: (next: boolean) => void;
  showItemDiscount: boolean;
  onToggleShowItemDiscount: (next: boolean) => void;
  showItemTax: boolean;
  onToggleShowItemTax: (next: boolean) => void;
  showItemNotes: boolean;
  onToggleShowItemNotes: (next: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Options
      </div>
      <Toggle label="Show logo" hint="Top of the invoice." value={showLogo} onChange={onToggleShowLogo} />
      <Toggle
        label="Show footer"
        hint="Thank-you line at the bottom."
        value={showFooter}
        onChange={onToggleShowFooter}
      />
      <Toggle
        label="Per-line discount badges"
        value={showItemDiscount}
        onChange={onToggleShowItemDiscount}
      />
      <Toggle
        label="Per-line tax badges"
        value={showItemTax}
        onChange={onToggleShowItemTax}
      />
      <Toggle
        label="Per-line notes"
        value={showItemNotes}
        onChange={onToggleShowItemNotes}
      />
    </div>
  );
}

function Toggle({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-3 rounded-md px-1 py-1.5 hover:bg-secondary/40">
      <span className="flex flex-col">
        <span className="text-xs font-medium text-foreground">{label}</span>
        {hint ? (
          <span className="text-[10px] leading-tight text-muted-foreground">{hint}</span>
        ) : null}
      </span>
      <Switch checked={value} onCheckedChange={onChange} />
    </label>
  );
}

function sameColor(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function normaliseHex(input: string): string {
  // <input type="color"> only accepts `#rrggbb`; anything else gets
  // silently clamped. Strip stray whitespace and reject obvious
  // non-hex strings so the picker doesn't reset to black.
  const trimmed = (input ?? '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  return '#3D466A';
}
